<script lang="ts" module>
	import { Locale, type Localized } from "@core/schema/Locale.js";
	import { htmlToRenderedText } from "@core/utils/htmlToRenderedText.js";
	import { makeInflect } from "@core/utils/makeInflect.js";

	const wordsPerMinute: Localized<number> = {
		[Locale.cs]: 200,
		[Locale.en]: 200,
	};

	const readTimeDescription: Localized<(count: number) => string> = {
		[Locale.cs]: makeInflect("minuta čtení", "minuty čtení", "minut čtení"),
		[Locale.en]: makeInflect("minute of reading", "minutes of reading", "minutes of reading"),
	};

	// Nad tímto počtem minut už neuvádíme přesný čas, ale zaokrouhlený údaj („10+ min čtení").
	const maxExactReadMinutes = 10;

	const longReadDescription: Localized<string> = {
		[Locale.cs]: `${maxExactReadMinutes}+ min čtení`,
		[Locale.en]: `${maxExactReadMinutes}+ min read`,
	};
</script>

<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import LinkCard from "@components/LinkCard.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getNewsDetailPath } from "@core/schema/paths/getNewsDetailPath.js";
	import { formatifyToText } from "@core/utils/formatifyToText.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getServices } from "../data/getServices.js";
	import type { BlogProps } from "./BlogProps.js";

	const { news, tenant }: BlogProps = $props();
	const contacts = $derived(getBookMeetingPeople(tenant));
</script>

<Layout>
	<Section background={Background.LightGradient} centered>
		{#snippet headline()}
			<Formatted text="Naše cg[[příspěvky]] na blogu" />
		{/snippet}
		<div
			class="grid justify-items-center sm:justify-items-stretch mt-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 sm:gap-4 mb-8 lg:mb-12 mx-auto"
		>
			{#each news as post (post.id)}
				<LinkCard className="[&_div]:max-w-full" title={post.title} href={getNewsDetailPath(post)}>
					{#snippet header()}
						{@const text = htmlToRenderedText(post.content)}
						{@const wordCount = text.split(/\s+/u).length}
						{@const readSpeed = wordsPerMinute[tenant.locale]}
						{@const minutes = Math.ceil(wordCount / readSpeed)}
						<span class="text-fstgreen self-end">
							{#if minutes > maxExactReadMinutes}
								{longReadDescription[tenant.locale]}
							{:else}
								{minutes}
								{readTimeDescription[tenant.locale](minutes)}
							{/if}
						</span>
					{/snippet}
					{#snippet desc()}
						<p class="line-clamp-6 text-ellipsis text-[0.875rem]/[1.125rem]">
							{formatifyToText(post.perex)}
						</p>
					{/snippet}
				</LinkCard>
			{/each}
		</div>
	</Section>
	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="S čím vám mimo jiné cg[[pomůžeme]]" />
		{/snippet}
	</Section>
	<ServicesCards cards={getServices(tenant)} />
	<Section noPadding="top" />
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
